import React from "react";
import compose from "lodash/fp/compose";
import gql from "graphql-tag";
import { graphql } from "react-apollo";
import { message } from "antd";
import get from "lodash/get";
import find from "lodash/find";
import isEqual from "lodash/isEqual";

import { Route } from "../../router";
import { convertMutationErrors } from "../shared/util/graphql";

import ScenarioView from "./ui/ScenarioView";

const setQuery = gql`
  {
    me {
      user {
        id
        licenseId
        isAdmin
      }
    }
    scenarioSet {
      id
      licenseId
      licensesIds
      name
      description
      definition
      sessionDataSet {
        id
        licenseId
        scenarioId
        sessionId
        data
        insertedAt
      }
    }
    licenseSet {
      id
      name
    }
  }
`;

const createMutation = gql`
  mutation($input: CreateScenarioInput!) {
    create: createScenario(input: $input) {
      scenario {
        id
      }
      errors {
        key
        message
      }
    }
  }
`;

const updateMutation = gql`
  mutation($input: UpdateScenarioInput!) {
    update: updateScenario(input: $input) {
      scenario {
        id
      }
      errors {
        key
        message
      }
    }
  }
`;

const deleteMutation = gql`
  mutation($input: DeleteScenarioInput!) {
    delete: deleteScenario(input: $input) {
      scenario {
        id
      }
    }
  }
`;

const generateMediaUploadUrlMutation = gql`
  mutation($input: GenerateMediaUploadUrlInput!) {
    generateMediaUploadUrl(input: $input) {
      url
    }
  }
`;

class C extends React.Component {
  state = {
    loading: false,
    scenarioDefinitionUpdate: undefined,
    mediaSelection: undefined,
  };

  handleCreateRequested = () =>
    this.props.history.push(`${this.props.match.path}/create`);

  handleCreateCancelled = () => this.props.history.push(this.props.match.path);

  handleCreateSubmitted = async (row, values) => {
    this.setState({ loading: true });
    const errors = await this.handleCreate(values);
    this.setState({ loading: false });
    if (errors) {
      return errors;
    }
    this.setState({ create: undefined });
    this.props.history.push(this.props.match.path);
    message.success("The scenario has been created");
  };

  handleCreate = async (values) => {
    const { data, createMutation } = this.props;
    const result = await createMutation({ variables: { input: values } });
    const errors = result.data.create.errors;
    if (errors) {
      return convertMutationErrors(errors);
    }
    await data.refetch();
    this.setState({ scenarioDefinitionUpdate: undefined });
  };

  handleViewRequested = (row) =>
    this.props.history.push(`${this.props.match.path}/view/${row.id}`);

  handleViewCancelled = () => this.props.history.push(this.props.match.path);

  handleViewSessionDataRequested = (row) =>
    this.props.history.push(
      `${this.props.match.path}/viewSessionData/${row.id}`
    );

  handleViewSessionDataCancelled = () =>
    this.props.history.push(this.props.match.path);

  handleEditRequested = (row) =>
    this.props.history.push(`${this.props.match.path}/edit/${row.id}`);

  handleEditCancelled = () => this.props.history.push(this.props.match.path);

  handleEditSubmitted = async (row, values) => {
    const { id } = row;
    this.setState({ loading: true });
    const errors = await this.handleEdit({ ...values, id });
    this.setState({ loading: false });
    if (errors) {
      return errors;
    }
    message.success("The scenario has been updated");
  };

  handleEdit = async ({ id, ...values }) => {
    const { data, updateMutation } = this.props;
    const input = { ...values, id };
    const result = await updateMutation({ variables: { input } });
    const errors = result.data.update.errors;
    if (errors) {
      return convertMutationErrors(errors);
    }
    await data.refetch();
    this.setState({ scenarioDefinitionUpdate: undefined });
  };

  handleScenarioEditorRequested = (row) =>
    !isEqual(row, {})
      ? this.props.history.push(
          `${this.props.match.path}/edit/${row.id}/scenario`
        )
      : this.props.history.push(`${this.props.match.path}/create/scenario`);

  handleScenarioEditorCancelled = () => this.props.history.push(".");

  handleScenarioEditorSubmitted = (scenarioDefinitionUpdate) => {
    this.setState({ scenarioDefinitionUpdate });
    this.props.history.push(".");
  };

  handleDeleteSubmitted = async (row) => {
    this.setState({ loading: true });
    await this.handleDelete(row);
    message.success("The scenario has been deleted");
    this.setState({ loading: false });
  };

  handleDelete = async ({ id }) => {
    const { data, deleteMutation } = this.props;
    await deleteMutation({ variables: { input: { id } } });
    await data.refetch();
  };

  handleMediaSelectionRequested = (callback) => {
    this.setState({ mediaSelection: callback });
  };

  handleGenerateUploadUrlRequested = async ({ file, licenseId }) => {
    const { generateMediaUploadUrlMutation } = this.props;
    const {
      data: {
        me: { user },
      },
    } = this.props;
    const input = { name: file.name };
    if (user.isAdmin) {
      input["licenseId"] = licenseId;
    }
    const variables = { input };
    const result = await generateMediaUploadUrlMutation({ variables });
    const url = get(result, "data.generateMediaUploadUrl.url");
    return url;
  };

  handleMediaSelectionCancelled = () => {
    const { mediaSelection: callback } = this.state;
    callback(undefined);
    this.setState({ mediaSelection: undefined });
  };

  handleMediaSelectionSubmitted = (result) => {
    const { mediaSelection: callback } = this.state;
    callback(result);
    this.setState({ mediaSelection: undefined });
  };

  render() {
    const { match, data } = this.props;
    const { loading, scenarioDefinitionUpdate, mediaSelection } = this.state;

    return (
      <Route
        path={`${match.path}/:action?/:id?/:subaction?`}
        render={(props) => {
          const {
            match: {
              params: { action, id, subaction },
            },
          } = props;
          const create = action === "create" && {};
          const view =
            action === "view" && find(data.scenarioSet, (x) => x.id === id);
          const viewSessionData =
            action === "viewSessionData" &&
            find(data.scenarioSet, (x) => x.id === id);
          const edit =
            action === "edit" && find(data.scenarioSet, (x) => x.id === id);

          let editScenario;
          if (isEqual([action, subaction], ["edit", "scenario"])) {
            editScenario = get(
              find(data.scenarioSet, (x) => x.id === id),
              "definition"
            );
          } else if (action === "create" && id === "scenario") {
            editScenario = "{}";
          }

          return (
            <ScenarioView
              match={match}
              data={data}
              loading={loading}
              create={create}
              scenarioDefinitionUpdate={scenarioDefinitionUpdate}
              onCreateRequested={this.handleCreateRequested}
              onCreateCancelled={this.handleCreateCancelled}
              onCreateSubmitted={this.handleCreateSubmitted}
              view={view}
              onViewRequested={this.handleViewRequested}
              onViewCancelled={this.handleViewCancelled}
              viewSessionData={viewSessionData}
              onViewSessionDataRequested={this.handleViewSessionDataRequested}
              onViewSessionDataCancelled={this.handleViewSessionDataCancelled}
              edit={edit}
              onEditRequested={this.handleEditRequested}
              onEditCancelled={this.handleEditCancelled}
              onEditSubmitted={this.handleEditSubmitted}
              editScenario={editScenario}
              onScenarioEditorRequested={this.handleScenarioEditorRequested}
              onScenarioEditorCancelled={this.handleScenarioEditorCancelled}
              onScenarioEditorSubmitted={this.handleScenarioEditorSubmitted}
              onDeleteSubmitted={this.handleDeleteSubmitted}
              mediaSelection={mediaSelection}
              onMediaSelectionRequested={this.handleMediaSelectionRequested}
              onMediaSelectionCancelled={this.handleMediaSelectionCancelled}
              onMediaSelectionSubmitted={this.handleMediaSelectionSubmitted}
            />
          );
        }}
      />
    );
  }
}

export default compose(
  graphql(createMutation, { name: "createMutation" }),
  graphql(updateMutation, { name: "updateMutation" }),
  graphql(deleteMutation, { name: "deleteMutation" }),
  graphql(generateMediaUploadUrlMutation, {
    name: "generateMediaUploadUrlMutation",
  }),
  graphql(setQuery, { options: { fetchPolicy: "network-only" } })
)(C);
